import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import IErrorDTO from "../../../../models/DTO/IErrorDTO";
import INotification from "../../../../models/INotification";
import OwTitle from "../../../shared/OwTitle/OwTitle";
import styled from "styled-components";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import SupplierContext from "../../../../contexts/SupplierContext";
import Typeahead from "../../../shared/Typeahead/Typeahead";
import IGetVendorsDTO from "../../../../models/DTO/IGetVendorsDTO";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import UserContext from "../../../../contexts/UserContext";
import IOnGetVendorsDTO from "../../../../models/DTO/IOnGetVendorsDTO";
import IVendorModel from "../../../../models/IVendorModel";
import OwSelectOption from "../../../shared/OwSelectOption/OwSelectOption";
import ICatalog from "../../../../models/ICatalog";
import IPrice_Group from "../../../../models/IPrice_Group";
import OwButton from "../../../shared/OwButton/OwButton";
import ICreateOrderDTO from "../../../../models/DTO/ICreateOrderDTO";
import { OwFnInput } from "../../../shared/OwFnInput/OwFnInput";
import IOnCreateOrderDTO from "../../../../models/DTO/IOnCreateOrderDTO";
import IOnGetCatalogsDTO from "../../../../models/DTO/IOnGetCatalogsDTO";
import IGetCatalogsDTO from "../../../../models/DTO/IGetCatalogsDTO";
import VendorContext from "../../../../contexts/VendorContext";
import { Socket } from "socket.io-client";

type Props = {
    cancelFn: () => void;
    setActivityIndicator: (activityIndicator: boolean) => void;
    socket: Socket;
    notifyFn: (data: IErrorDTO | null, _notification?: INotification, isFormatted?: boolean) => void;
    isOnline: boolean;
    toggleSidebar: (shouldBeVisible?: boolean) => void;
    setSidebarComponent: (component: any) => void;
};

export const CreateOrderFnSidebar: React.FC<Props> = (props) => {
    const supplierModel = useContext(SupplierContext);
    const userModel = useContext(UserContext);
    const vendorModel = useContext(VendorContext);

    const [foundAccounts, setFoundAccounts] = useState<IVendorModel[]>([]);
    const [selectedAccount, setSelectedAccount] = useState<IVendorModel | undefined>();
    const [catalogOptions, setCatalogOptions] = useState<ICatalog[]>([]);
    const [selectedCatalog, setSelectedCatalog] = useState<ICatalog>();
    const [selectedPriceGroup, setSelectedPriceGroup] = useState<IPrice_Group>();
    const [availablePriceGroups, setAvailablePriceGroups] = useState<IPrice_Group[]>([]);
    const [hasSingleCatalogLock, setHasSingleCatalogLock] = useState(false);
    const [hasSinglePriceGroupLock, setHasSinglePriceGroupLock] = useState(false);
    const [isCreateActive, setIsCreateActive] = useState(false);
    const [poNumber, setPoNumber] = useState("");

    useEffect(() => {
        const sockets: { endpoint: EndpointsEnum; fn: any }[] = [
            { endpoint: EndpointsEnum.VendorsOnGetVendors, fn: onGetAccounts },
            { endpoint: EndpointsEnum.OrderOnCreateOrder, fn: onCreateOrder },
            { endpoint: EndpointsEnum.CatalogsOnGetCatalogs, fn: onGetCatalogs },
        ];

        for (const socket of sockets) {
            props.socket.on(socket.endpoint, socket.fn);
        }

        return () => {
            for (const socket of sockets) {
                props.socket.removeListener(socket.endpoint, socket.fn);
            }
        };
    }, []);

    useEffect(() => {
        if (selectedCatalog && catalogOptions && !selectedPriceGroup) {
            let _selectedCatalog: ICatalog | undefined;

            for (const catalog of catalogOptions) {
                if (catalog.id.toString() === selectedCatalog.id.toString()) {
                    _selectedCatalog = catalog;
                }
            }

            if (_selectedCatalog?.Price_Groups) {
                setAvailablePriceGroups(_selectedCatalog.Price_Groups);
            }
        }
    }, [catalogOptions]);

    const getAccounts = (name: string) => {
        // TODO make work with browser offline mode.
        const encodedName = encodeURIComponent(name);

        const payload: IGetVendorsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            queryString: `name=${encodedName}`,
            limit: 20,
        };

        props.socket.emit(EndpointsEnum.VendorsGetVendors, payload);
    };

    const onGetAccounts = (data: IOnGetVendorsDTO) => {
        const _foundAccounts = data.supplierVendorTokens.map((tokenData) => tokenData.Vendor!);

        setFoundAccounts(_foundAccounts);
    };

    const getCatalogs = () => {
        const payload: IGetCatalogsDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            vendorGuid: vendorModel.guid,
            callMeta: {
                view: "CreateOrderFnSidebar",
                fn: "getCatalogs",
            },
        };

        props.socket.emit(EndpointsEnum.CatalogsGetCatalogs, payload);
    };

    const onGetCatalogs = (data: IOnGetCatalogsDTO) => {
        setCatalogOptions(data.catalogs);
    };

    const selectAccount = (account: IVendorModel) => {
        setSelectedAccount(account);
        getCatalogs();

        if (account.Catalog && !account.Price_Groups_Map) {
            setSelectedCatalog(account.Catalog);
            setHasSingleCatalogLock(true);

            if (account.Price_Group) {
                setSelectedPriceGroup(account.Price_Group);
                setHasSinglePriceGroupLock(true);
            }
        }
    };

    const selectCatalog = (catalogId: string) => {
        let _selectedCatalog: ICatalog | undefined;

        for (const catalog of catalogOptions) {
            if (catalog.id.toString() === catalogId) {
                _selectedCatalog = catalog;
            }
        }

        setSelectedCatalog(_selectedCatalog);

        if (_selectedCatalog?.Price_Groups) {
            setAvailablePriceGroups(_selectedCatalog.Price_Groups);
        }
    };

    const selectPriceGroup = (priceGroupId: string) => {
        let _selectedPriceGroup: IPrice_Group | undefined;

        if (selectedCatalog?.Price_Groups) {
            for (const priceGroup of selectedCatalog!.Price_Groups) {
                if (priceGroup.id.toString() === priceGroupId) {
                    _selectedPriceGroup = priceGroup;
                }
            }
        }

        setSelectedPriceGroup(_selectedPriceGroup);
    };

    const createOrder = () => {
        if (selectedAccount && selectedCatalog && selectedPriceGroup) {
            const payload: ICreateOrderDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                vendorGuid: selectedAccount.guid,
                catalogGuid: selectedCatalog.guid,
                priceGroupGuid: selectedPriceGroup.guid,
                po_no: poNumber,
            };

            props.socket.emit(EndpointsEnum.OrderCreateOrder, payload);
        }
    };

    const onCreateOrder = (data: IOnCreateOrderDTO) => {
        // The Orders screen will handle transitioning the user to the actual order
        // after creation, and will toggle off the sidebar.
    };

    const handleChange = (e: ChangeEvent<any>) => {
        const value = e.target.value;
        const name = e.target.name;

        switch (name) {
            case "poNumber":
                setPoNumber(value);
                break;
        }
    };

    const closeSidebar = () => {
        props.cancelFn();
    };

    return (
        <CreateOrderSidebarWrapper className={"CreateOrderFnSidebar"}>
            <div className={"d-flex"}>
                <OwTitle title={"create_order"} />
                <div className={"ms-auto"}>
                    <OwButton fn={closeSidebar} centerIconClass={"fas fa-times"} className={"btn-danger"} />
                </div>
            </div>

            <div className={"mt-2"}>
                <OwLabel title={"vendor"} isSupplierVendorOverride={true} supplierModel={supplierModel} isBold={true} />
                <div>
                    <Typeahead
                        isSelectableFn={() => true}
                        typeaheadApiFn={getAccounts}
                        typeaheadItems={foundAccounts}
                        titleProp={"name"}
                        subTitleProp={"customer_internal_id"}
                        scrollableList={true}
                        maxResults={100}
                        selectedItemFn={selectAccount}
                    />
                </div>
            </div>

            <div className={"mt-2"}>
                <OwLabel title={"catalog"} isBold={true} />
                {hasSingleCatalogLock ? (
                    <div>{selectedCatalog?.name}</div>
                ) : (
                    <OwSelectOption options={catalogOptions} titleProp={"name"} valueProp={"id"} fn={selectCatalog} name={"catalog"} />
                )}
            </div>

            <div className={"mt-2"}>
                <OwLabel title={"price_group"} isBold={true} />
                {hasSinglePriceGroupLock ? (
                    <div>{selectedPriceGroup?.value}</div>
                ) : availablePriceGroups.length ? (
                    <OwSelectOption
                        options={availablePriceGroups}
                        titleProp={"value"}
                        valueProp={"id"}
                        fn={selectPriceGroup}
                        name={"priceGroup"}
                    />
                ) : (
                    ""
                )}
            </div>

            <div className={"mt-2"}>
                <OwLabel title={"po_no"} isBold={true} />
                <OwFnInput title={"poNumber"} fn={handleChange} name={"poNumber"} value={poNumber} />
            </div>

            <div className={"mt-2"}>
                <OwButton title={"create"} fn={createOrder} active={isCreateActive} disableOnActive={true} />
            </div>
        </CreateOrderSidebarWrapper>
    );
};

const CreateOrderSidebarWrapper = styled.div`
    padding: 0.5rem;
`;
